export type Offer =
  | { kind: "free"; recommended: boolean }
  | { kind: "donation"; recommended: boolean; suggestedCents: number }
  | {
      kind: "paid";
      variant: "standard";
      recommended: boolean;
      membershipCents: number;
      processingFeeCents: number;
      /** For `defaultQuantity` memberships: N × membership + **one** fee. */
      totalCents: number;
      /** Sponsoring is more than one membership, not a different product, so
       * an applicant who registered to sponsor starts at 2. */
      defaultQuantity: number;
      maxQuantity: number;
    };

export type PayInfo = {
  name: string;
  email: string;
  org: string;
  requestedType: string | null;
  offers: Offer[];
  /** Extras added on top of a paid membership, billed in the same session.
   * Sponsorship used to be one; it is the membership quantity now. */
  addons: {
    donation?: { suggestedCents: number; minimumCents: number };
  };
  /** True only when the backend is running a Stripe test key, so the test-card
   * hint can never be shown to someone paying with a real card. */
  testMode: boolean;
};

/** One membership a sponsor has paid for, and who it is for. */
export type SponsoredMembership = {
  id: number;
  status: "unassigned" | "assigned" | "claimed";
  beneficiary_email: string;
  beneficiary_name: string;
  created_at: string;
  assigned_at: string | null;
  claimed_at: string | null;
};

export type Subscription = {
  id: string;
  kind: "membership" | "sponsorship" | "donation" | "other";
  status:
    | "active"
    | "past_due"
    | "unpaid"
    | "canceled"
    | "incomplete"
    | "trialing"
    | string;
  quantity: number;
  amountCents: number;
  interval: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
};

export type MyBilling = {
  tier: "free" | "gift" | "paid";
  status: "pending_payment" | "active" | "lapsed";
  /** Where a gift came from: an admin's outright grant, or a sponsor who is
   * paying for it. Empty when the membership is not a gift. */
  giftSource: "" | "admin" | "sponsorship" | "organization";
  /** In the directory. Approval sets it; signing up alone does not. */
  listed: boolean;
  applicationStatus: "pending" | "approved" | "declined" | null;
  approved: boolean;
  /** Whether the Membership item offers checkout. Paying for your own
   * membership needs an approved application, because a membership is a
   * directory listing and listings are reviewed. Sponsoring and donating are
   * not gated on anything. */
  canBuyMembership: boolean;
  /** What a membership costs, so the line beside the Subscribe button is never
   *  a number somebody typed. Re-pricing on the settings page has to reach the
   *  place people are asked to pay. */
  pricing: {
    membershipCents: number;
    serviceFeeCents: number;
    totalCents: number;
  };
  hasStripeCustomer: boolean;
  /** Everything they are paying for, read from Stripe: their own membership, a
   *  sponsorship-only subscription bought without one, a standing donation.
   *  Empty when Stripe is unreachable or unconfigured — the page falls back to
   *  the stored snapshot below. */
  subscriptions: Subscription[];
  renewsAt: string | null;
  cancelAtPeriodEnd: boolean;
  payments: {
    id: number;
    created_at: string;
    kind: string;
    amount_cents: number;
    simulated: boolean;
      /** Stripe's own PDF, when this was Stripe's money to bill. Absent for
     *  the imported history, which Squarespace charged as bare charges. */
    invoiceUrl: string | null;
  }[];
};

export type AdminBilling = {
  totals: {
    allTimeRevenueCents: number;
    paymentCount: number;
    activePaidMembers: number;
    estimatedAnnualCents: number;
  };
  subscribers: {
    member_id: number;
    name: string;
    email: string;
    member_status: string;
    subscription_status: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    amount_cents: number | null;
  }[];
  ledger: {
    id: number;
    created_at: string;
    member_id: number;
    member_name: string;
    kind: string;
    amount_cents: number;
    simulated: boolean;
    stripe_ref: string | null;
  }[];
};

// A refund is a negative row on the ledger, and reads "-$56.25", not "$-56.25".
export const dollars = (cents: number) =>
  `${cents < 0 ? "-" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;
